import React from 'react';
import styled, { css } from 'styled-components';
import {
    PropertiesPanelAccordion,
    PropertiesPanelAccordionContainer,
    PropertiesPanelAccordionContent,
    PropertiesPanelAccordionHeader
} from './propertiesPanelAccordion';
import { PropertiesPanelChannel } from './propertiesPanelChannel';
import { PropertiesPanelCheckboxProperty, PropertiesPanelCheckboxPropertyProps } from './propertiesPanelCheckboxProperty';
import { PropertiesPanelColorProperty, PropertiesPanelColorPropertyProps } from './propertiesPanelColorProperty';
import { PropertiesPanelDivider, PropertiesPanelDividerProps } from './propertiesPanelDivider';
import { PropertiesPanelIconProperty, PropertiesPanelIconPropertyProps } from './propertiesPanelIconProperty';
import { PropertiesPanelImageProperty, PropertiesPanelImagePropertyProps } from './propertiesPanelImageProperty';
import { PropertiesPanelInlineIconProperty, PropertiesPanelInlineIconPropertyProps } from './propertiesPanelInlineIconProperty';
import { PropertiesPanelNumberProperty, PropertiesPanelNumberPropertyProps } from './propertiesPanelNumberProperty';
import { PropertiesPanelPointProperty, PropertiesPanelPointPropertyProps } from './propertiesPanelPointProperty';
import { PropertiesPanelRangeProperty, PropertiesPanelRangePropertyProps } from './propertiesPanelRangeProperty';
import { PropertiesPanelResourceProperty, PropertiesPanelResourcePropertyProps } from './propertiesPanelResourceProperty';
import { PropertiesPanelSwitch, PropertiesPanelSwitchProperty, PropertiesPanelSwitchPropertyProps } from './propertiesPanelSwitchProperty';
import { PropertiesPanelSection } from './propertiesPanelSection';
import { panel } from './panelTheme';
import { PropertiesPanelActionProperty, PropertiesPanelActionPropertyProps } from './propertiesPanelActionProperty';
import { PropertiesPanelBareSelect, PropertiesPanelSelectProperty, PropertiesPanelSelectPropertyProps } from './propertiesPanelSelectProperty';
import { PropertiesPanelTextProperty, PropertiesPanelTextPropertyProps } from './propertiesPanelTextProperty';

export type PropertiesPanelContainerProps = {
    $quiet?: boolean;
};
export const PropertiesPanelContainer = styled.div<PropertiesPanelContainerProps>`
    ${(props) =>
        !props.$quiet &&
        css`
            & > ${PropertiesPanelAccordionContainer} {
                border: 1px solid ${(props) => props.theme.app.borderColor};
                border-radius: 0;

                &:has(+ ${PropertiesPanelAccordionContainer}) {
                    border-top-left-radius: 4px;
                    border-top-right-radius: 4px;
                }

                &:not(:has(+ ${PropertiesPanelAccordionContainer})) {
                    border-bottom-left-radius: 4px;
                    border-bottom-right-radius: 4px;
                }

                & + ${PropertiesPanelAccordionContainer}:has(+ ${PropertiesPanelAccordionContainer}) {
                    border-radius: 0;
                }

                & > ${PropertiesPanelAccordionHeader} {
                    padding: 6px 8px;
                    border: 0;
                }

                & > ${PropertiesPanelAccordionContent} {
                    padding: 0;
                    background-color: ${(props) => props.theme.app.component.propertiesPanel.accordion.content.background};

                    & > *:not(${PropertiesPanelAccordionContainer}) {
                        padding: 4px 8px;
                    }

                    & > ${PropertiesPanelAccordionContainer} {
                        border-top: 1px solid ${(props) => props.theme.app.borderColor};

                        & > ${PropertiesPanelAccordionHeader} {
                            padding: 6px 8px;
                        }

                        & > ${PropertiesPanelAccordionContent} {
                            padding: 0 8px;
                        }
                    }
                }
            }
        `}
`;

type GenericProperty<Type, Props> = {
    id: string;
    type: Type;
} & Props;

type IconProperty = GenericProperty<'icon', PropertiesPanelIconPropertyProps>;
type TextProperty = GenericProperty<'text', PropertiesPanelTextPropertyProps>;
type RangeProperty = GenericProperty<'range', PropertiesPanelRangePropertyProps>;
type NumberProperty = GenericProperty<'number', PropertiesPanelNumberPropertyProps>;
type CheckboxProperty = GenericProperty<'checkbox', PropertiesPanelCheckboxPropertyProps>;
type ColorProperty = GenericProperty<'color', PropertiesPanelColorPropertyProps>;
type ImageProperty = GenericProperty<'image', PropertiesPanelImagePropertyProps>;
type PointProperty = GenericProperty<'point', PropertiesPanelPointPropertyProps>;
type SelectProperty = GenericProperty<'select', PropertiesPanelSelectPropertyProps>;
type InlineIconProperty = GenericProperty<'inline-icon', PropertiesPanelInlineIconPropertyProps>;
type SwitchProperty = GenericProperty<'switch', PropertiesPanelSwitchPropertyProps>;
type ResourceProperty = GenericProperty<'resource', PropertiesPanelResourcePropertyProps>;
type ActionProperty = GenericProperty<'action', PropertiesPanelActionPropertyProps>;
type DividerProperty = GenericProperty<'divider', Omit<PropertiesPanelDividerProps, 'id'>>;

/**
 * IL CANALE — un parametro che si può anche dipingere con una texture.
 *
 * Non è un gruppo: un gruppo è un titolo che introduce delle righe, questo È una riga — con il
 * proprio valore in vista e, dentro, ciò che il valore non dice. Le tre parti sono dichiarate
 * separate perché sono tre cose diverse: `head` resta sempre visibile, `properties` sono i valori
 * che il canale governa, `map` è il file che lo dipinge con le proprie impostazioni. È questa
 * separazione a disegnare il divisore nel cassetto — non c'è nessuna regola che indovini dove
 * passa il confine.
 */
type ChannelProperty = GenericProperty<
    'channel',
    {
        label: React.ReactNode;
        head?: Property;
        properties?: (Property | PropertiesGroup)[];
        map?: (Property | PropertiesGroup)[];
        defaultIsOpen?: boolean;
    }
>;

type Property =
    | IconProperty
    | TextProperty
    | RangeProperty
    | NumberProperty
    | CheckboxProperty
    | ColorProperty
    | ImageProperty
    | PointProperty
    | SelectProperty
    | InlineIconProperty
    | SwitchProperty
    | ResourceProperty
    | ChannelProperty
    | ActionProperty
    | DividerProperty;

export type PropertiesGroup = GenericProperty<
    'group',
    {
        header: React.ReactNode;
        properties: (Property | PropertiesGroup)[];
        defaultIsOpen?: boolean;
        /** Non si apre: quello che dice sta tutto nella testata. */
        fixed?: boolean;
        /**
         * L'INTERRUTTORE DELLA FAMIGLIA, in testata. Ce l'hanno i gruppi che si ACCENDONO oltre
         * che aprirsi — la vernice, il velo, l'anisotropia — e sta in testa perché è ciò che
         * decide se il resto vale: dentro, sarebbe una riga come le altre in un elenco che
         * invece governa.
         */
        trailing?: Property;
    }
>;

/**
 * IL CAPITOLO — «SCENE», «SHADING», «SETTINGS». Non è un gruppo che si apre: è l'indice di una
 * colonna lunga, una parola in alto che dice di che cosa parlano le card sotto. Serve perché otto
 * card di fila senza capitoli sono un elenco senza indice: si scorre e non si sa più dove si è.
 */
export type PropertiesSection = GenericProperty<
    'section',
    {
        title: React.ReactNode;
        properties: (Property | PropertiesGroup)[];
    }
>;

// SU UNA RIGA, UNA SPUNTA È UN INTERRUTTORE. Una spunta porta con sé la propria etichetta e si
// prende la riga intera; qui il nome ce l'ha già la riga — quello del canale, quello della card —
// e ripeterlo accanto al comando sarebbe la stessa parola due volte.
// UN VALORE CHE SI LEGGE E NON SI TOCCA, in testata: il tipo di una luce, la specie di un
// materiale. Non è un campo spento — un campo spento promette che un giorno si accende — è una
// parola messa dove la si cerca.
const HeaderValue = styled.span`
    display: inline-flex;
    align-items: center;
    flex: none;
    height: 20px;
    padding: 0 8px;
    border-radius: 999px;
    background: ${panel.field};
    color: ${panel.ink};
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
`;

const BareSwitch: React.FC<{ property: Property; labelledBy?: string; ariaLabel?: string }> = ({ property, labelledBy, ariaLabel }) => {
    if (property.type === 'text' && !property.onChange) return <HeaderValue>{property.value}</HeaderValue>;

    // UN MENU NELLA TESTATA VA NUDO: il nome ce l'ha già la card, e la riga completa porterebbe
    // una seconda etichetta accanto alla prima.
    if (property.type === 'select') {
        return (
            <PropertiesPanelBareSelect
                id={property.id}
                options={property.options}
                value={property.value}
                onChange={property.onChange}
                disabled={property.disabled}
                selectCustomProps={property.selectCustomProps}
                ariaLabel={ariaLabel ?? textOf(property.label)}
            />
        );
    }

    if (property.type !== 'checkbox' && property.type !== 'switch') return <PropertySwitch property={property} />;

    return (
        <PropertiesPanelSwitch
            id={property.id}
            checked={property.checked}
            labelledBy={labelledBy}
            ariaLabel={ariaLabel}
            disabled={property.disabled}
            onChange={property.onChange}
        />
    );
};

// L'ANTEPRIMA SULLA RIGA È LA TEXTURE VERA, presa dallo slot che sta nel cassetto: due sorgenti
// per la stessa immagine sarebbero due cose da tenere d'accordo, e la prima a sbagliare sarebbe
// quella che si guarda.
const firstPreview = (properties?: (Property | PropertiesGroup)[]) => {
    const image = properties?.find((property) => property.type === 'image') as ImageProperty | undefined;
    return image?.images?.[0]?.src;
};

/** Il nome di una card serve anche a chi non vede il suo interruttore, ma solo quando è una parola. */
const textOf = (node: React.ReactNode) => (typeof node === 'string' ? node : undefined);

type PropertySwitchProps = {
    property: PropertiesSection | PropertiesGroup | Property;
};
const PropertySwitch: React.FC<PropertySwitchProps> = ({ property }) => {
    const type = property.type;

    switch (type) {
        case 'icon':
            return <PropertiesPanelIconProperty {...property} />;
        case 'text':
            return <PropertiesPanelTextProperty {...property} />;
        case 'range':
            return <PropertiesPanelRangeProperty {...property} />;
        case 'number':
            return <PropertiesPanelNumberProperty {...property} />;
        case 'checkbox':
            return <PropertiesPanelCheckboxProperty {...property} />;
        case 'switch':
            return <PropertiesPanelSwitchProperty {...property} />;
        case 'resource':
            return <PropertiesPanelResourceProperty {...property} />;
        case 'action':
            return <PropertiesPanelActionProperty {...property} />;
        case 'divider':
            return <PropertiesPanelDivider />;
        case 'select':
            return <PropertiesPanelSelectProperty {...property} />;
        case 'color':
            return <PropertiesPanelColorProperty {...property} />;
        case 'image':
            return <PropertiesPanelImageProperty {...property} />;
        case 'point':
            return <PropertiesPanelPointProperty {...property} />;
        // LA CHIAVE DELLE IMPOSTAZIONI DI MAPPA PORTA LA POSIZIONE, perché lì gli id si ripetono: uno
        // slot di texture emette la mappa e la sua versione mobile con lo stesso nome, e due fratelli
        // con la stessa chiave sono due righe che React non sa più distinguere.
        case 'channel':
            return (
                <PropertiesPanelChannel
                    id={property.id}
                    label={property.label}
                    defaultIsOpen={property.defaultIsOpen}
                    head={property.head && <BareSwitch property={property.head} labelledBy={`${property.id}-name`} />}
                    values={property.properties?.map((child) => (
                        <PropertySwitch key={child.id} property={child} />
                    ))}
                    mapSettings={property.map?.map((child, position) => (
                        <PropertySwitch key={`${child.id}-${position}`} property={child} />
                    ))}
                    hasMap={Boolean(property.map?.length)}
                    preview={firstPreview(property.map)}
                />
            );
        case 'group':
            return (
                <PropertiesPanelAccordion
                    key={property.id}
                    header={property.header}
                    defaultIsOpen={property.defaultIsOpen}
                    fixed={property.fixed}
                    trailing={property.trailing && <BareSwitch property={property.trailing} ariaLabel={textOf(property.header)} />}
                >
                    {property.properties.map((property) => (
                        <PropertySwitch key={property.id} property={property} />
                    ))}
                </PropertiesPanelAccordion>
            );
        case 'section':
            return (
                <PropertiesPanelSection key={property.id} title={property.title}>
                    {property.properties.map((property) => (
                        <PropertySwitch key={property.id} property={property} />
                    ))}
                </PropertiesPanelSection>
            );
        case 'inline-icon':
            return <PropertiesPanelInlineIconProperty {...property} />;
        default:
            throw new Error(`Unsupported property: ${type}`);
    }
};

/**
 * Quello che può stare DENTRO una card. Un capitolo no: un capitolo contiene card, non il
 * contrario, e questo tipo è quello che i pannelli passano da anni ai loro gruppi.
 */
export type PropertiesPanelPropertyConfig = PropertiesGroup | Property;

/**
 * UNA RIGA SOLA — ciò che non è un gruppo. Serve a chi COMPONE un canale: il controllo di testa
 * è una riga, non un ramo, e il tipo lo dice invece di lasciarlo a un cast.
 */
export type PropertiesPanelLeafConfig = Property;

/** Quello che può stare al PRIMO livello di un pannello: le card, e i capitoli che le raccolgono. */
export type PropertiesPanelContent = PropertiesSection | PropertiesPanelPropertyConfig;
export type PropertiesPanelProps = {
    /**
     * enable the quiet design variant
     */
    quiet?: boolean;
    properties: PropertiesPanelContent[];
};
export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({ quiet, properties }) => {
    return (
        <PropertiesPanelContainer $quiet={quiet}>
            {properties.map((property) => {
                return <PropertySwitch key={property.id} property={property} />;
            })}
        </PropertiesPanelContainer>
    );
};
